<script setup lang="ts">
import type { Navigation, NavigationGroup } from './types';
import NavigationSubMenu from '~/components/Aggregates/Navigation/NavigationSubMenu.vue';
import MobileNavigation from '~/components/Aggregates/Navigation/MobileNavigation.vue';
import { cn } from '~/lib/utils';

// Define navigation exclusion mapping
const navigationExclusions = {
  wholesale: 'Wholesale',
  madeToMeasure: 'Made to Measure'
};

const props = defineProps<{
  primaryNavigation?: Navigation[];
}>();

const { state, clearActiveSubNavigation } = useNavigation();
const { setOverflowHidden } = useOverflowHidden();
const { dealerContent } = useDealerContent();
const route = useRoute();

const showBg = ref(false);

const filteredNavigation = computed(() => {
  if (!dealerContent.value || !Array.isArray(props.primaryNavigation)) {
    return props.primaryNavigation;
  }

  return props.primaryNavigation.filter((nav: Navigation) => {
    if (
      dealerContent.value?.productClassificationExclusions?.includes('wholesale') &&
      nav.label === navigationExclusions.wholesale
    ) {
      return false;
    }

    if (
      dealerContent.value?.productClassificationExclusions?.includes('madeToMeasure') &&
      nav.label === navigationExclusions.madeToMeasure
    ) {
      return false;
    }

    return true;
  });
});

const handleNavigationEvent = (data: any) => {
  clearActiveSubNavigation();

  if (!data) {
    showBg.value = false;
    setOverflowHidden(false);
  } else {
    showBg.value = true;
    setOverflowHidden(true);
  }
};

const isActive = (label: string) => {
  return route.fullPath.includes(label.split(' ').join('-').toLowerCase());
};
</script>

<template>
  <NavigationMenu
    class="hidden md:flex"
    @update:model-value="handleNavigationEvent"
    ref="element"
    role="navigation"
    aria-label="Navigation"
  >
    <div class="group" :data-state="showBg ? 'open' : 'closed'">
      <OverlayBlur />
    </div>
    <NavigationMenuList>
      <NavigationMenuItem v-for="navigation in filteredNavigation" :key="navigation.label" class="group">
        <NavigationMenuTrigger :class="cn('p-4', '')" :data-active="isActive(navigation.label)">{{
          navigation.label
        }}</NavigationMenuTrigger>
        <NavigationMenuContent>
          <div class="left-0 w-screen rounded-br-[180px]">
            <div class="grid-row-1 container grid grid-cols-4 items-start justify-start gap-5 py-8">
              <!-- Primary Navigation -->
              <div class="flex flex-col">
                <NavigationSubMenu
                  v-for="group in navigation.items"
                  class="!border-0 !pl-0"
                  :key="group.groupLabel"
                  :items="group.items"
                  :title="group.groupLabel"
                  :active-item="state.activeSubNavigation"
                  state-key="activeSubNavigation"
                />
              </div>

              <!-- Secondary Navigation -->
              <NavigationSubMenu
                v-if="state.activeSubNavigation?.items"
                :items="state.activeSubNavigation.items"
                :title="`All ${state.activeSubNavigation.title}`"
                :active-item="state.activeSubNavigation2"
                :link="state.activeSubNavigation.link"
                state-key="activeSubNavigation2"
              />

              <!-- Tertiary Navigation -->
              <NavigationSubMenu
                v-if="state.activeSubNavigation2?.items"
                :items="state.activeSubNavigation2.items"
                :title="state.activeSubNavigation2.title"
                :active-item="state.activeSubNavigation3"
                :link="state.activeSubNavigation2.link"
                state-key="activeSubNavigation3"
              />

              <!-- Quaternary Navigation -->
              <NavigationSubMenu
                v-if="state.activeSubNavigation3?.items"
                :items="state.activeSubNavigation3.items"
                :title="state.activeSubNavigation3.title"
                :active-item="state.activeSubNavigation3"
                :link="state.activeSubNavigation3.link"
              />
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    </NavigationMenuList>
  </NavigationMenu>

  <!-- Mobile Navigation -->
  <MobileNavigation :primary-navigation="filteredNavigation" />
</template>
